import { useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Path } from 'core/constants/path.constants';
import { EProductOrigin } from 'core/model/enums/gtm.enum';
import {
  IAddedProduct,
  IBaseProduct,
  IMetadata,
  IProduct,
  IUnitMeasure,
} from 'core/model/interfaces/product.interface';
import {
  buildOptimisticCartAction,
  changeProductAction,
  changeProductActionFromCart,
  optimisticRemoveProductAction,
  removeProductAction,
  removeProductFromCartAction,
} from 'core/store/cart';
import { IChangeProductActionParams } from 'core/store/cart/types';
import { resetIdleShipping } from 'core/store/checkout';
import { toogleCart } from 'core/store/layout';
import { useAppDispatch } from 'core/store/store.utils';
import { changeSelectedUnitMeasure } from 'screens/product-detail/product-detail.utils';
import { triggerSelectedProduct } from 'shared/utils/gtm-events/product.events';
import {
  addProductToShoppingCartEvent,
  removeProductFromShoppingCartEvent,
} from 'shared/utils/gtm-events/shopping-cart.events';
import { triggerInsiderAddToCart, triggerInsiderRemoveFromCart } from 'shared/utils/insider.utils';

export interface IUseProductActionsParams {
  listName?: string;
  origin: EProductOrigin;
  loading?: boolean;
}

export interface IOnChangeCounterQuantity {
  item: IBaseProduct;
  currentQuantity: number;
  newQuantity: number;
  stock: number;
  unitMeasureCode: IUnitMeasure['code'];
  index?: number;
  metadata?: IMetadata;
  suggestedProductType?: string;
}

interface IOnChangeQuantity extends Omit<IOnChangeCounterQuantity, 'index' | 'metadata'> {
  metadata: IMetadata;
}

export interface IOnDeleteProductParams extends Pick<IOnChangeCounterQuantity, 'item' | 'unitMeasureCode'> {
  metadata: IMetadata;
  quantityBeforeRemoval: number;
}

interface IChangeProductActionRef {
  arg: IAddedProduct | null;
  abort: () => void;
}

export const useProductActions = ({ listName = 'null', origin, loading = false }: IUseProductActionsParams) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const shouldRefreshCheckoutDetails = pathname.includes(Path.CHECKOUT);

  const changeProductActionRef = useRef<IChangeProductActionRef>();

  const onDetailProduct = (product: IProduct) => {
    triggerSelectedProduct({ product, listName, origin });
    if (!loading) {
      dispatch(toogleCart(false));
      navigate(`${Path.PRODUCT}/${product?.sku}`);
    }
  };

  const onDeleteProduct = async (params: IOnDeleteProductParams) => {
    const { metadata, item, quantityBeforeRemoval, unitMeasureCode } = params;

    const product = changeSelectedUnitMeasure(item, unitMeasureCode);
    const inCart = origin === EProductOrigin.SHOPPING_CART || origin === EProductOrigin.SUGGESTED_PRODUCTS_CART;
    if (inCart) {
      await dispatch(removeProductFromCartAction({ ...product, shouldRefreshCheckoutDetails }));
    } else {
      dispatch(optimisticRemoveProductAction(product));
      dispatch(removeProductAction({ sku: product.sku, sourceId: product.sourceId }));
    }
    removeProductFromShoppingCartEvent({
      product,
      quantityBeforeRemoval,
      metadata: { ...metadata, origin },
    });

    triggerInsiderRemoveFromCart(product, quantityBeforeRemoval);
  };

  const onChangeProduct = ({
    item,
    currentQuantity,
    newQuantity,
    unitMeasureCode,
    metadata,
    suggestedProductType,
    stock,
  }: IOnChangeQuantity) => {
    if (!item) return;
    const hasReachedMaxStock = newQuantity === stock;
    const isDecrementingProducts = newQuantity < currentQuantity;
    const { sku } = item;
    const product = changeSelectedUnitMeasure(item, unitMeasureCode);

    if (isDecrementingProducts) {
      const quantityRemoved = currentQuantity - newQuantity;
      removeProductFromShoppingCartEvent({
        product,
        quantityBeforeRemoval: quantityRemoved,
        metadata,
      });
      triggerInsiderRemoveFromCart(product, quantityRemoved);
    } else {
      const quantityAdded = newQuantity - currentQuantity;
      addProductToShoppingCartEvent({
        product,
        quantity: quantityAdded,
        metadata,
        hasReachedMaxStock,
      });
      triggerInsiderAddToCart(product, quantityAdded);
    }
    const changingFromCart = [EProductOrigin.SHOPPING_CART, EProductOrigin.SUGGESTED_PRODUCTS_CART].includes(origin);

    const changeProductActionParams: IChangeProductActionParams = {
      product,
      sku,
      quantity: newQuantity,
      listName,
      origin,
      unitMeasureCode,
      metadata,
      suggestedProductType,
      stock,
    };

    if (changingFromCart)
      dispatch(
        changeProductActionFromCart({
          ...changeProductActionParams,
          shouldRefreshCheckoutDetails,
        }),
      );
    else {
      dispatch(buildOptimisticCartAction(changeProductActionParams));
      dispatch(resetIdleShipping());

      changeProductActionRef.current = dispatch(changeProductAction(changeProductActionParams));
    }
  };

  const onChangeCounterQuantity = ({
    item,
    currentQuantity,
    newQuantity,
    unitMeasureCode,
    index = 0,
    metadata,
    stock,
  }: IOnChangeCounterQuantity) => {
    const computedMetadata = metadata ?? { origin, index: index + 1, itemListName: listName };
    if (newQuantity <= 0)
      onDeleteProduct({ item, quantityBeforeRemoval: currentQuantity, metadata: computedMetadata, unitMeasureCode });
    else {
      onChangeProduct({
        item,
        currentQuantity,
        newQuantity,
        unitMeasureCode,
        metadata: computedMetadata,
        stock,
      });
    }
  };

  const onAbort = (sku: string, unitMeasureId: string) => {
    if (
      changeProductActionRef.current?.arg?.sku === sku &&
      changeProductActionRef.current?.arg?.unitMeasureCode === unitMeasureId
    )
      changeProductActionRef.current?.abort();
  };

  return { onAbort, onChangeCounterQuantity, onDetailProduct, onDeleteProduct };
};
